import React, { useEffect, useState } from "react";

import { FormProvider, useForm } from "react-hook-form";
import Modal from "components/foundation/Modal/Modal";
import Toast, { useToast } from "components/foundation/Toast";
import WithDrawModal from "../WithDrawModal/WithDrawModal";
import UserProfileForm from "./UserProfileForm";
import UserProfileMobileForm from "./UserProfileMobileForm";
import useMediaView from "features/common/hooks/useMediaView";
import useMyProfileDetailQuery from "features/my-page/queries/useMyProfileDetailQuery";
import useUserUpdateMutation from "features/user/hooks/mutations/useUserUpdateMutation";
import useBeforeUnload from "features/common/hooks/useBeforeUnload";

export interface ProfileFormData {
  phone: string;
  password: string;
  passwordConfirm: string;
}
interface UserProfileFormCardProps {
  password: string;
}

const UserProfileFormCard = ({ password }: UserProfileFormCardProps) => {
  const { open, onOpenChange, onOpenToast } = useToast();

  const isMobile = useMediaView("small");
  const {
    blocking,
    process,
    isBlock,
    enableUnloadTracking,
    disableUnloadTracking,
  } = useBeforeUnload({
    isInitialTracking: false,
  });

  const { data: profileData } = useMyProfileDetailQuery();
  const { mutate: updateUser } = useUserUpdateMutation();
  const methods = useForm<ProfileFormData>({
    defaultValues: {
      phone: profileData.data.result.phoneNumber,
      password: "",
      passwordConfirm: "",
    },
    mode: "onChange",
  });

  const [isInvalidModalOpen, setIsInvalidModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isWithDrawModalOpen, setIsWithDrawModalOpen] = useState(false);

  const handleFormSubmit = (data: ProfileFormData) => {
    if (data.phone.length < 11) return;
    if (data.password !== data.passwordConfirm) {
      setIsInvalidModalOpen(true);
      return;
    }

    setIsSaveModalOpen(true);
  };

  useEffect(() => {
    methods.formState.isDirty && enableUnloadTracking();

    return () => {
      disableUnloadTracking();
    };
  }, [disableUnloadTracking, enableUnloadTracking, methods.formState.isDirty]);

  return (
    <Toast.Provider duration={2000}>
      <Toast.Message open={open} onOpenChange={onOpenChange}>
        변경 내용이 저장되었습니다.
      </Toast.Message>
      {/* 화면 이동시 나타나는 모달창 */}
      <Modal.Root open={isBlock}>
        <Modal.Body
          title="화면 이동 시 변경된 내용은
저장되지 않습니다. 이동하시겠습니까?"
          onCancel={blocking}
          onConfirm={process}
        >
          확인 버튼 클릭 시 변경 내용을 저장하지 않고 이동합니다. 변경내용
          저장을 원하실 경우, 취소 후 저장 버튼을 클릭해 주세요.
        </Modal.Body>
      </Modal.Root>

      {/* 탈퇴시 나타나는 모달창 */}
      <WithDrawModal
        isOpen={isWithDrawModalOpen}
        setIsOpen={setIsWithDrawModalOpen}
      />

      {/* 변경하려는 비밀번호가 동일하지 않을떄 나타나는 모달창 */}
      <Modal.Root
        open={isInvalidModalOpen}
        onOpenChange={setIsInvalidModalOpen}
      >
        <Modal.Body
          title="비밀번호 오류"
          onConfirm={() => {
            setIsInvalidModalOpen(false);
          }}
        >
          비밀번호가 동일하지 않습니다. 다시 입력해 주세요.
        </Modal.Body>
      </Modal.Root>

      {/* 변경사항을 저장하는 모달창 */}
      <Modal.Root open={isSaveModalOpen} onOpenChange={setIsSaveModalOpen}>
        <Modal.Body
          title="변경 내용을 저장하시겠습니까?"
          onCancel={() => {
            setIsSaveModalOpen(false);
          }}
          onConfirm={() => {
            updateUser(
              {
                phoneNumber: methods.getValues("phone"),
                currentPassword: password,
                newPassword: methods.getValues("password"),
              },
              {
                onSuccess: () => {
                  setIsSaveModalOpen(false);
                  onOpenToast();
                  disableUnloadTracking();
                },
              },
            );
          }}
        ></Modal.Body>
      </Modal.Root>

      {/* 메인 폼 */}
      <form onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <FormProvider {...methods}>
          {!isMobile && (
            <UserProfileForm
              setIsWithDrawModalOpen={setIsWithDrawModalOpen}
              defaultData={profileData.data.result}
              currentPassword={password}
            />
          )}
          {isMobile && (
            <UserProfileMobileForm
              defaultData={profileData.data.result}
              currentPassword={password}
            />
          )}
        </FormProvider>
      </form>
    </Toast.Provider>
  );
};

export default UserProfileFormCard;
